import _ from "lodash";
import React, { useEffect, useRef } from "react";

import classes from "./ParentLogin.scss";
import GoogleLoginBtn from "UIComponents/GoogleLoginBtn";
import MicrosoftLoginBtn from "UIComponents/MicrosoftLoginBtn";
import CustomLoginButton from "UIComponents/CustomLoginButton";
import GridLayoutWrapper from "UIComponents/GridLayoutWrapper";
import {FormTextInput} from "UIComponents/InputTextField";
import PhoneInputField from "UIComponents/PhoneInputField";
import { Button, DropdownMenu } from "@toddle-edu/ds-web";
import { GOOGLE_CLIENT_ID, LEX, LEX_PILOT } from "Constants";

import {
  LexColored,
  ProfileOutlined,
  SchoolOutlined,
  MailColored,
} from "@toddle-edu/ds-icons";
import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import { isNewFamilySignUpFlowEnabled } from "config";
import classNames from "classnames";

const styles = {
  editContainerStyle: {
    display: "block",
  },
};

const ParentLogin = props => {
  const {
    onGoogleSignInCallBack,
    onGoogelFailureCallBack,
    onMicroSoftSignInCallBack,
    onMicroSoftFailureCallBack,
    onLexPilotSignInCallBack,
    renderTradeMarkText,
    updateCurrentScreen,
    onLexSignInCallBack,
    customStyle: { headerStyleV2 } = {},
    renderBackButtonComponent,
    isFamilyClassInviteRoute,
    isLexEnabled,
    isGoogleEnabled,
    isMicrosoftEnabled,
    updateInputField,
    showErrorToast,
    data: { email = "", phoneNo = "", shouldRenderPhoneInput = false },
    renderErrorComponent,
    checkEmailVerification,
    userType,
    generateEmailOTPForSignIn,
    generateOtpForSignIn,
    isNewLoginFlowEnabled,
    isLoginWithPhoneDisabled,
    isOrgWorkspaceLoginPage,
    loginButtonsCount,
    renderLoginMethods,
    isOTPLoginEnabled,
    isPasswordLoginEnabled,
    isOrgWorkspaceLoginEnabled,
    organizationName,
    setAuthRegion,
    updateToddleInitiatedValue,
    enableLoginFlowV2,
    isCreateParentAccountEnabled,
  } = props;

  const lexAuthRef = useRef();

  const lexPilotAuthRef = useRef();

  const inputRefs = {};

  const onLexLoginClick = () => {
    if (lexAuthRef && lexAuthRef.current) {
      updateToddleInitiatedValue(true);
      const onAuthClick = _.get(
        lexAuthRef,
        "current.refs.wrappedInstance.onAuthClick"
      );
      onAuthClick();
    }
  };

  const onLexPilotLoginClick = () => {
    if (lexPilotAuthRef && lexPilotAuthRef.current) {
      updateToddleInitiatedValue(true);
      const onAuthClick = _.get(
        lexPilotAuthRef,
        "current.refs.wrappedInstance.onAuthClick"
      );
      onAuthClick();
    }
  };

  const getLexOptions = () => {
    return [
      {
        key: LEX_PILOT,
        label: "Lex (Global)",
        icon: <LexColored size="xx-small" />,
      },
      {
        key: LEX,
        label: "Lex (Brazil)",
        icon: <LexColored size="xx-small" />,
      },
    ];
  };

  const onClickLexOption = ({ key }) => {
    if (key === LEX_PILOT) {
      onLexPilotLoginClick();
    } else if (key === LEX) {
      onLexLoginClick();
    }
  };

  const getScreenTypeOnCreateAccountClick = () => {
    if (isNewFamilySignUpFlowEnabled) {
      // for class invite, we don't need to go to verifyParentInviteCodeScreen because we have region in the url of class invite
      return isFamilyClassInviteRoute
        ? "createParentAccount"
        : "verifyParentInviteCode";
    } else {
      return "createParentAccount";
    }
  };

  const onCreateAccountClick = () => {
    setAuthRegion("");
    updateCurrentScreen({ type: getScreenTypeOnCreateAccountClick() });
  };

  const onBackClick = () => {
    const { updateCurrentScreen, resetScreen, isNewLoginFlowEnabled } = props;
    if (isNewLoginFlowEnabled) {
      resetScreen("parentLogin");
    }
    updateCurrentScreen({ type: "loginHome", paramsToBeRemoved: ["usertype"] });
  };

  const updateEmail = value => {
    updateInputField({
      email: value,
    });
  };

  const updatePhone = value => {
    updateInputField({
      phoneNo: value,
    });
  };

  const checkPhoneVerification = value => {
    return value.length < 6 ? "Please enter a valid phone number" : "";
  };

  const isValid = () => {
    let errorCount = 0;
    const formRef =
      shouldRenderPhoneInput && !isLoginWithPhoneDisabled
        ? inputRefs["phoneNo"]
        : inputRefs["email"];
    if (formRef) {
      errorCount += formRef.isValid();
    }
    return errorCount;
  };

  const onNextClick = async () => {
    const errorCount = isValid();
    showErrorToast({ errorCount });
    if (errorCount > 0) {
      return;
    }

    // check enabled login methods before initiating login flow
    if (shouldRenderPhoneInput && !isLoginWithPhoneDisabled) {
      const { isSuccess } = await generateOtpForSignIn();
      if (isSuccess) {
        updateCurrentScreen({ type: "loginFormV2" });
      }
    } else if (isOTPLoginEnabled) {
      const { isSuccess } = await generateEmailOTPForSignIn({ userType });
      if (isSuccess) {
        updateCurrentScreen({ type: "loginFormV2" });
      }
    } else if (isPasswordLoginEnabled) {
      updateInputField({ email }, "loginForm");
      updateCurrentScreen({ type: "loginForm" });
    } else {
      updateInputField({ shouldRenderPhoneInput: true });
    }
  };

  const updatePhoneInput = (
    { phoneNo },
    {
      dialCode,
      e: {
        nativeEvent: { data },
      },
    }
  ) => {
    updatePhone(phoneNo);
    updateEmail(phoneNo.slice(dialCode.length));

    if (data && isNaN(parseInt(data))) {
      updateEmail(phoneNo.slice(dialCode.length) + data);
    }
  };

  useEffect(() => {
    if (isNewLoginFlowEnabled && !isLoginWithPhoneDisabled) {
      updateInputField({
        shouldRenderPhoneInput: !!(
          // return true if email is a number or if email is not there check phoneNo is a valid number
          ((email && !isNaN(email)) || (!email && phoneNo && !isNaN(phoneNo)))
        ),
      });
    }
  }, [phoneNo, email]);

  const buttonsContainerStyle = classNames({
    [classes.buttonsContainer]: true,
    [classes.buttonsContainerV2]: isNewLoginFlowEnabled,
  });

  const bottomContainerStyles = classNames({
    [classes.bottomContainer]: true,
    [classes.bottomContainerV2]: isNewLoginFlowEnabled,
  });

  const onYourSchoolClick = () => {
    const { updateCurrentScreen } = props;
    updateCurrentScreen({ type: "schoolCodeForm" });
  };

  const getEmailLabelHelperText = () => {
    if (isLoginWithPhoneDisabled) {
      if (isOTPLoginEnabled) {
        return "Sign in with email OTP";
      } else {
        return "Sign in with email";
      }
    } else {
      if (isOTPLoginEnabled || isPasswordLoginEnabled) {
        return "Sign in with email/phone OTP";
      } else {
        return "Sign in with phone OTP";
      }
    }
  };

  const getEmailInputPlaceholder = () => {
    if (isLoginWithPhoneDisabled) {
      return "Enter your email id";
    } else {
      if (isOTPLoginEnabled || isPasswordLoginEnabled) {
        return "Enter your email id or phone number";
      } else {
        return "Enter your phone number";
      }
    }
  };

  const onLoginClick = () => {
    updateCurrentScreen({
      type: "loginFormV3",
    });
  };

  const showHelperText = isOrgWorkspaceLoginPage
    ? loginButtonsCount > 0
    : isNewLoginFlowEnabled;

  const isEmailLoginFlowEnabled =
    isOTPLoginEnabled || isPasswordLoginEnabled || !isLoginWithPhoneDisabled;

  const containerStyle = {
    paddingTop: isOrgWorkspaceLoginPage ? "13%" : "19.5%",
  };

  return (
    <div
      className={classes.container}
      style={containerStyle}
      role="main"
      aria-label="Family Login Page"
    >
      <div>
        {renderBackButtonComponent({
          onClick: onBackClick,
          buttonText: isOrgWorkspaceLoginPage
            ? organizationName
            : "Toddle home",
          ariaLabel: "Go back to usertype selection screen",
        })}
        <div style={headerStyleV2}>
          {isOrgWorkspaceLoginPage
            ? "Sign in to your family account"
            : "Family account"}
        </div>
        <div
          className={buttonsContainerStyle}
          role="group"
          aria-label="Login methods"
        >
          {showHelperText ? (
            <div className={classes.helperText}>{"Sign in with"}</div>
          ) : null}
          {!isOrgWorkspaceLoginPage ? (
            <GridLayoutWrapper
              noOfColumns={2}
              itemsPerRow={2}
              stretchItemsInLastRow={false}
              role="list"
              aria-label="Login options list"
            >
              {enableLoginFlowV2 && isEmailLoginFlowEnabled && (
                <CustomLoginButton
                  key={"EMAIL_LOGIN_BTN"}
                  data-test-id={"button-login-email"}
                  icon={<MailColored size="x-small" />}
                  onClick={onLoginClick}
                  buttonText={
                    !isLoginWithPhoneDisabled &&
                    (isOTPLoginEnabled || isPasswordLoginEnabled)
                      ? "Email/Phone number"
                      : isLoginWithPhoneDisabled
                      ? "Email"
                      : "Phone number"
                  }
                />
              )}

              {isGoogleEnabled && (
                <GoogleLoginBtn
                  key={"GOOGLE_LOGIN_BTN"}
                  clientId={GOOGLE_CLIENT_ID}
                  onSuccess={onGoogleSignInCallBack}
                  onFailure={onGoogelFailureCallBack}
                  prompt="select_account"
                />
              )}

              {isMicrosoftEnabled && (
                <MicrosoftLoginBtn
                  key={"MICROSOFT_LOGIN_BTN"}
                  onSuccess={onMicroSoftSignInCallBack}
                  onFailure={onMicroSoftFailureCallBack}
                />
              )}

              {isLexEnabled && (
                <CustomLoginButton
                  key={"LEX_LOGIN_BTN"}
                  icon={<LexColored size="x-small" />}
                  onClick={onLexLoginClick}
                  buttonText={"Lex"}
                  isSplitButton={true}
                  overlay={
                    <DropdownMenu
                      options={getLexOptions()}
                      onClick={onClickLexOption}
                    />
                  }
                />
              )}

              {isOrgWorkspaceLoginEnabled && (
                <CustomLoginButton
                  key={"SCHOOL_LOGIN_BTN"}
                  data-test-id={"button-login-school"}
                  icon={<SchoolOutlined size="x-small" />}
                  buttonText={"Your school"}
                  onClick={onYourSchoolClick}
                />
              )}
            </GridLayoutWrapper>
          ) : (
            renderLoginMethods()
          )}
        </div>

        {isNewLoginFlowEnabled ? (
          isCreateParentAccountEnabled ? (
            <div>
              {showHelperText ? (
                <div className={classes.orContainer}>
                  <div className={classes.orLineContainer} />
                  <div className={classes.orText}>or</div>
                  <div className={classes.orLineContainer} />
                </div>
              ) : null}
              {isEmailLoginFlowEnabled && !enableLoginFlowV2 ? (
                <div className={bottomContainerStyles}>
                  <div className={classes.emailContainer}>
                    <div className={classes.emailText}>
                      {getEmailLabelHelperText()}
                    </div>
                    <div className={classes.inputRowContainer}>
                      <div className={classes.inputField}>
                        {shouldRenderPhoneInput && !isLoginWithPhoneDisabled ? (
                          <PhoneInputField
                            placeholder={"Enter your phone number"}
                            value={phoneNo}
                            name={"phoneNo"}
                            key={"phoneNo"}
                            renderErrorComponent={renderErrorComponent}
                            updateInputField={updatePhoneInput}
                            ref={ref => (inputRefs["phoneNo"] = ref)}
                            showErrorText={true}
                            customValidation={checkPhoneVerification}
                            editContainerStyle={styles.editContainerStyle}
                            inputProps={{
                              autoFocus: true,
                            }}
                          />
                        ) : (
                          <FormTextInput
                            placeholder={getEmailInputPlaceholder()}
                            ref={ref => (inputRefs["email"] = ref)}
                            value={email}
                            name={"email"}
                            customValidation={checkEmailVerification}
                            renderErrorComponent={renderErrorComponent}
                            showErrorText={true}
                            size={"large"}
                            updateInputField={({ email }) => {
                              if (!isLoginWithPhoneDisabled) {
                                updatePhone(email);
                              }
                              updateEmail(email);
                            }}
                            onEnter={onNextClick}
                            leadingIcon={
                              <ProfileOutlined
                                size="xx-small"
                                variant="subtle"
                              />
                            }
                            error={"Enter valid email"}
                            autoComplete={"off"}
                            autoFocus={true}
                            testId={"input-login-email"}
                          />
                        )}
                      </div>
                      <Button
                        variant={"destructive"}
                        disabled={email.length === 0}
                        onClick={onNextClick}
                        size={"large"}
                        className={classes.submitButton}
                        testId={"login-email-next"}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
              <div>
                {`Don't have an account?`}
                <span className={classes.signupButton}>
                  <Button
                    data-test-id="button-forgot-password"
                    variant={"inline-progressive"}
                    size={"small"}
                    onClick={onCreateAccountClick}
                    aria-label="Create new parent acount"
                  >{`Create new`}</Button>
                </span>
              </div>
            </div>
          ) : null
        ) : (
          <CustomLoginButton
            icon={<ProfileOutlined size="x-small" />}
            buttonText={"Create a new account"}
            onClick={onCreateAccountClick}
          />
        )}

        <ThirdPartyAuth
          serviceType="LEX"
          ref={lexAuthRef}
          onSuccessCallBack={onLexSignInCallBack}
        />
        <ThirdPartyAuth
          serviceType="LEX_PILOT"
          ref={lexPilotAuthRef}
          onSuccessCallBack={onLexPilotSignInCallBack}
        />
      </div>
      <div>
        {renderTradeMarkText({
          align: "left",
        })}
      </div>
    </div>
  );
};

export default ParentLogin;
